import { isValidMapPosition } from './navigationUrl';

let googleMapsPromise;

export function isBrowserMapConfigured() {
  return Boolean(String(process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY || '').trim());
}

export function loadGoogleMaps() {
  if (typeof window === 'undefined') return Promise.reject(new Error('MAP_BROWSER_UNAVAILABLE'));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!isBrowserMapConfigured()) return Promise.reject(new Error('MAP_KEY_NOT_CONFIGURED'));
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-apparify-google-maps="true"]');
    const finish = () => window.google?.maps
      ? resolve(window.google.maps)
      : reject(new Error('MAP_LOAD_FAILED'));

    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => reject(new Error('MAP_LOAD_FAILED')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.dataset.apparifyGoogleMaps = 'true';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY)}&v=weekly`;
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('MAP_LOAD_FAILED')), { once: true });
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

export class BrowserMapAdapter {
  async renderPinPicker(container, { position, onSelect }) {
    const maps = await loadGoogleMaps();
    const fallback = { lat: 14.5995, lng: 120.9842 };
    const initial = position || fallback;
    const map = new maps.Map(container, {
      center: initial,
      zoom: position ? 17 : 11,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    const marker = new maps.Marker({ map, position: initial, draggable: true });

    const publish = (location) => {
      const next = { latitude: location.lat(), longitude: location.lng() };
      marker.setPosition(location);
      onSelect(next);
    };
    const clickListener = map.addListener('click', (event) => publish(event.latLng));
    const dragListener = marker.addListener('dragend', (event) => publish(event.latLng));

    return () => {
      clickListener.remove();
      dragListener.remove();
      marker.setMap(null);
    };
  }

  async renderOperationalMap(container, initialState = {}) {
    const maps = await loadGoogleMaps();
    const fallback = { lat: 14.5995, lng: 120.9842 };
    const map = new maps.Map(container, {
      center: fallback,
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
    });
    const markers = new Map();
    let routeLine = null;
    let routeIdentity = null;
    let followIdentity = null;
    let followEnabled = true;
    let followPosition = null;
    let pointsIdentity = null;
    let initialized = false;

    const dragListener = map.addListener('dragstart', () => { followEnabled = false; });

    const recenter = () => {
      followEnabled = true;
      if (!followPosition) return;
      map.panTo(followPosition);
      if (!Number.isFinite(map.getZoom?.()) || map.getZoom() < 15) map.setZoom(16);
    };

    // Trip-overview stops: a numbered circle whose colour says where the stop
    // stands. Points without a tone keep the default marker.
    const TONE_COLOURS = { store: '#d97706', done: '#a8a29e', current: '#059669', upcoming: '#1c1917' };
    const markerLabel = (point) => (point.markerLabel
      ? { text: String(point.markerLabel), color: '#ffffff', fontSize: '12px', fontWeight: '700' }
      : null);
    const markerIcon = (point) => {
      if (String(point.id) !== String(followIdentity)) {
        // SymbolPath.CIRCLE is 0, so "is it there" must not be a truthiness test.
        if (!point.tone || maps.SymbolPath?.CIRCLE === undefined) return undefined;
        return {
          path: maps.SymbolPath.CIRCLE,
          scale: point.tone === 'current' ? 13 : 11,
          fillColor: TONE_COLOURS[point.tone] || TONE_COLOURS.upcoming,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        };
      }
      const heading = point.headingDegrees == null ? null : Number(point.headingDegrees);
      if (heading != null && Number.isFinite(heading) && maps.SymbolPath?.FORWARD_CLOSED_ARROW) {
        return {
          path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
          rotation: heading,
          scale: 6,
          fillColor: '#059669',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        };
      }
      if (maps.SymbolPath?.CIRCLE === undefined) return undefined;
      return {
        path: maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#059669',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      };
    };

    const update = ({ points = [], encodedPolyline = null, followPointId = null } = {}) => {
      const priorFollowIdentity = followIdentity;
      followIdentity = followPointId == null ? null : String(followPointId);
      if (priorFollowIdentity !== followIdentity) followEnabled = true;
      const bounds = new maps.LatLngBounds();
      const validPoints = points.filter(isValidMapPosition);
      const nextPointsIdentity = validPoints.map((point, index) => `${point.id ?? index}:${Number(point.latitude).toFixed(6)}:${Number(point.longitude).toFixed(6)}`).join('|');
      const pointsChanged = nextPointsIdentity !== pointsIdentity;
      pointsIdentity = nextPointsIdentity;
      const activeMarkerIds = new Set();
      followPosition = null;
      validPoints.forEach((point, index) => {
        const identity = String(point.id ?? `point-${index}`);
        activeMarkerIds.add(identity);
        const position = { lat: Number(point.latitude), lng: Number(point.longitude) };
        bounds.extend(position);
        const icon = markerIcon(point);
        const existing = markers.get(identity);
        const label = markerLabel(point);
        if (existing) {
          existing.setPosition(position);
          existing.setTitle?.(point.label || '');
          existing.setIcon?.(icon || null);
          existing.setLabel?.(label);
        } else {
          markers.set(identity, new maps.Marker({
            map, position, title: point.label || '', ...(icon ? { icon } : {}), ...(label ? { label } : {}),
          }));
        }
        if (identity === followIdentity) followPosition = position;
      });
      markers.forEach((marker, identity) => {
        if (!activeMarkerIds.has(identity)) {
          marker.setMap(null);
          markers.delete(identity);
        }
      });

      const nextRouteIdentity = encodedPolyline || null;
      const routeChanged = nextRouteIdentity !== routeIdentity;
      if (routeChanged) {
        routeIdentity = nextRouteIdentity;
        const path = encodedPolyline ? decodeGooglePolyline(encodedPolyline) : [];
        if (routeLine) routeLine.setMap(null);
        routeLine = path.length ? new maps.Polyline({
          map, path, strokeColor: '#059669', strokeOpacity: 0.85, strokeWeight: 5,
        }) : null;
      }

      const routePath = routeLine?.getPath?.();
      if (routePath?.forEach) routePath.forEach((position) => bounds.extend(position));
      if (followIdentity && followPosition && followEnabled) {
        recenter();
      } else if ((validPoints.length || routeLine) && (!initialized || routeChanged || pointsChanged)) {
        // Room for the overlays on top (view toggle) and the sheet below.
        map.fitBounds(bounds, { top: 96, right: 40, bottom: 64, left: 40 });
        if (validPoints.length === 1 && !routeLine) map.setZoom(16);
      }
      initialized = true;
    };
    update(initialState);
    return {
      update,
      recenter,
      destroy: () => {
        dragListener.remove();
        markers.forEach((marker) => marker.setMap(null));
        markers.clear();
        if (routeLine) routeLine.setMap(null);
      },
    };
  }
}

export function decodeGooglePolyline(encoded = '') {
  const coordinates = [];
  let index = 0;
  let latitude = 0;
  let longitude = 0;
  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index <= encoded.length);
    latitude += (result & 1) ? ~(result >> 1) : (result >> 1);
    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index <= encoded.length);
    longitude += (result & 1) ? ~(result >> 1) : (result >> 1);
    coordinates.push({ lat: latitude / 1e5, lng: longitude / 1e5 });
  }
  return coordinates;
}
